import React, { useCallback, useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";

import { FullWidthLoader } from "../../components/full-width-loader";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import { SectionHeading } from "../../components/section-heading";
import Selector from "../../components/selector";

import { useUserPropertyTickers } from "../../components/user-tickers-providers";

import { AdminKeycloakAPIPromiseClient } from "../../gen/witbooking/backoffice/adminkeycloak/v1/adminkeycloak_grpc_web_pb";

import { GetUsersByPropertyTickerRequest } from "../../gen/witbooking/backoffice/adminkeycloak/v1/adminkeycloak_pb";
import { ACCESS_DENIED, getPropertyRpt } from "../../lib/auth";
import { useGrpcClient } from "../../lib/grpc";

import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "internalmenu.integrations.verifyUserManager",
      defaultMessage: "Verify User Manager",
    },
    propertyTickerSelectPlaceholder: {
      id: "verifyUserManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
    ListOfUser: { id: "verifyUserManager.ListOfUser", defaultMessage: "List of user" },
    permissions: { id: "verifyUserManager.permissions", defaultMessage: "Permissions" },
    email: { id: "verifyUserManager.email", defaultMessage: "Email" },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;

const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;

type Option = { value: string; label: string };

function VerifyUserManager() {
  const keycloakAuthz = useKeycloakAuthz();
  const userPropertyTickersList = useUserPropertyTickers();

  const adminKeycloackManagerGrpcClient = useGrpcClient(AdminKeycloakAPIPromiseClient);

  const [loading, setLoading] = useState(false);

  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);
  const [propertyTickerOption, setPropertyTickerOption] = useState<Option | null | undefined>(
    undefined
  );

  const [dataUserFound, setDataUserFound] = useState<any[]>([]);

  const handleSelectPropertyTicker = useCallback(
    selectedOption => setPropertyTickerOption(selectedOption),
    [setPropertyTickerOption]
  );

  useEffect(() => {
    const fetchPropertyTickers = async () => {
      setLoading(true);

      setPropertyTickerOptions(
        userPropertyTickersList.map(option => ({ value: option, label: option }))
      );

      setLoading(false);
    };
    fetchPropertyTickers();
  }, [userPropertyTickersList]);

  useEffect(() => {
    const fetchUserByProperty = async () => {
      if (!propertyTickerOption || !propertyTickerOption.value) {
        return;
      }
      setLoading(true);
      try {
        const token = await getPropertyRpt(propertyTickerOption.value, keycloakAuthz);
        const usersByProperty = await adminKeycloackManagerGrpcClient.getUsersByPropertyTicker(
          new GetUsersByPropertyTickerRequest(),
          { Authorization: `Bearer ${token}`, "x-property": propertyTickerOption.value }
        );
        const dataUserFound = usersByProperty.getUsersList().map(option => ({
          id: option.getId(),
          email: option.getEmail(),
          groups: option.getGroupsList().map(group => ({
            id: group.getId(),
            path: group.getPath(),
            name: group.getName(),
          })),
        }));
        setDataUserFound(dataUserFound);
      } catch (e) {
        if (e === ACCESS_DENIED) {
          console.error("Access denied", e);
        } else {
          console.error("Error retrieving children", e);
        }
      }
      setLoading(false);
    };
    fetchUserByProperty();
  }, [propertyTickerOption, keycloakAuthz, adminKeycloackManagerGrpcClient]);

  if (loading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
          </Label>
          <Selector
            options={propertyTickerOptions}
            value={propertyTickerOption}
            onChange={handleSelectPropertyTicker}
            placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
          />
        </Container>

        <Container>
          <Label>{<FormattedMessage {...messages.ListOfUser} />}</Label>
          <table>
            <tr>
              <th>{<FormattedMessage {...messages.email} />}</th>
              <th>{<FormattedMessage {...messages.permissions} />}</th>
            </tr>
            {dataUserFound.map((user: any) => (
              <tr>
                <th>{user?.email}</th>
                <th>
                  {user?.groups?.map((group: any) => {
                    return `${group?.name}, `;
                  })}
                </th>
              </tr>
            ))}
          </table>
        </Container>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(VerifyUserManager);
